"use client";
import React, { useEffect, useState } from "react";
import { getSortedProjects } from "@/lib/mdxProject";
import Link from "next/link";
import Titleheading from "@/app/project/Titleheading";

const ProjectsPage = ({ initialProjects, initialTotalCount, initialPage }) => {
  const [currentProjects, setCurrentProjects] = useState(initialProjects || []); // Fallback to empty array
  const [loading, setLoading] = useState(false); // Add a loading state
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("ALL"); // Default is ALL
  const [showSubcategories, setShowSubcategories] = useState(false); // Hide subcategories by default
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [activeLocation, setActiveLocation] = useState(null);
  const [activeSize, setActiveSize] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialPage || 1); // Fallback to page 1
  const [totalPages, setTotalPages] = useState(
    Math.ceil(initialTotalCount / 9) || 1
  ); // Fallback to 1 page
  const projectsPerPage = 9;

  const [activeFilter, setActiveFilter] = useState(""); // Track which filter is active

  const useOptions = {
    ARCHITECTURE: [
      "MasterPlanning",
      "Commercial Retail",
      "Commercial Office",
      "Institution",
      "Educational",
      "Religious",
      "Hospitality",
      "Residential",
      "Café / Restaurants",
    ],
    INTERIORS: [
      "Residential",
      "Retail",
      "Office",
      "Cinema Halls",
      "Café / Restaurants",
      "Hospitality",
    ],
    STYLING: ["Events", "Interiors", "Fashion"],
  };

  const locationOptions = {
    ARCHITECTURE: ["New York", "Los Angeles", "Chicago"],
    INTERIORS: ["San Francisco", "Miami", "Seattle"],
    STYLING: ["Paris", "Milan", "London"],
  };

  const sizeOptions = {
    ARCHITECTURE: ["500 sqft", "1000 sqft", "5000 sqft"],
    INTERIORS: ["300 sqft", "800 sqft", "1200 sqft"],
    STYLING: ["Small", "Medium", "Large"],
  };

  const statusOptions = {
    ARCHITECTURE: ["Completed", "Ongoing", "Planned"],
    INTERIORS: ["Completed", "Ongoing", "Planned"],
    STYLING: ["Completed", "Ongoing", "Planned"],
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const allCategories = ["ALL", "ARCHITECTURE", "INTERIORS", "STYLING"];
      setCategories(allCategories);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true); // Start loading

      const categoryQuery = activeCategory === "ALL" ? "ALL" : activeCategory;
      const subcategoryQuery = activeSubcategory ? activeSubcategory : "";
      const locationQuery = activeLocation ? activeLocation : "";
      const sizeQuery = activeSize ? activeSize : "";
      const statusQuery = activeStatus ? activeStatus : "";

      const { projects, totalCount } = await getSortedProjects(
        currentPage,
        projectsPerPage,
        "", // Search query (not currently used)
        categoryQuery, // Pass the category, "ALL" will only match projects with "ALL" in their categories
        subcategoryQuery, // Pass the subcategory (Use)
        locationQuery, // Pass the location
        sizeQuery, // Pass the size
        statusQuery // Pass the status
      );

      setCurrentProjects(projects);
      setTotalPages(Math.ceil(totalCount / projectsPerPage));
      setLoading(false); // End loading
    };

    fetchProjects();
  }, [
    currentPage,
    activeCategory,
    activeSubcategory,
    activeLocation,
    activeSize,
    activeStatus,
  ]);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    setShowSubcategories(category !== "ALL"); // Hide subcategories when ALL is selected
    setActiveSubcategory(null); // Reset subcategory when changing category
    setActiveLocation(null); // Reset location when changing category
    setActiveSize(null); // Reset size when changing category
    setActiveStatus(null); // Reset status when changing category
    setCurrentPage(1); // Reset to the first page whenever the category changes
    setActiveFilter(""); // Reset active filter when category is changed
  };

  const handleFilterClick = (filter) => {
    setActiveFilter((prevFilter) => (prevFilter === filter ? "" : filter));
  };

  const handleOptionSelect = (filterType, option) => {
    if (filterType === "use") {
      setActiveSubcategory(option);
      setActiveLocation(null); // Clear other filters
      setActiveSize(null);
      setActiveStatus(null);
    } else if (filterType === "location") {
      setActiveLocation(option);
      setActiveSubcategory(null); // Clear other filters
      setActiveSize(null);
      setActiveStatus(null);
    } else if (filterType === "size") {
      setActiveSize(option);
      setActiveSubcategory(null); // Clear other filters
      setActiveLocation(null);
      setActiveStatus(null);
    } else if (filterType === "status") {
      setActiveStatus(option);
      setActiveSubcategory(null); // Clear other filters
      setActiveLocation(null);
      setActiveSize(null);
    }
    setCurrentPage(1); // Reset to the first page whenever an option is selected
    setActiveFilter(""); // Close the dropdown after an option is selected
  };

  return (
    <>
      <Titleheading />

      {/* Main categories */}
      <div className="tabs grid grid-cols-3 lg:flex justify-center gap-4 p-4">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => handleCategoryClick(category)}
            className={`py-2 px-4 text-sm ${
              activeCategory === category
                ? "bg-black text-white"
                : "bg-white text-black"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Subcategories */}
      {showSubcategories && (
        <div className="subtabs flex justify-center space-x-4 p-4">
          <div className="relative">
            <button
              onClick={() => handleFilterClick("use")}
              className={`py-2 px-4 text-sm ${
                activeSubcategory
                  ? "bg-green-500 text-white"
                  : "bg-white text-green-500"
              }`}
            >
              Use
            </button>
            {activeFilter === "use" && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 shadow-lg z-10">
                <ul className="divide-y divide-gray-200">
                  {useOptions[activeCategory]?.map((useCategory) => (
                    <li
                      key={useCategory}
                      onClick={() => handleOptionSelect("use", useCategory)}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {useCategory}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Location Dropdown */}
          <div className="relative">
            <button
              onClick={() => handleFilterClick("location")}
              className={`py-2 px-4 text-sm ${
                activeLocation
                  ? "bg-green-500 text-white"
                  : "bg-white text-green-500"
              }`}
            >
              Location
            </button>
            {activeFilter === "location" && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 shadow-lg z-10">
                <ul className="divide-y divide-gray-200">
                  {locationOptions[activeCategory]?.map((location) => (
                    <li
                      key={location}
                      onClick={() => handleOptionSelect("location", location)}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {location}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Size Dropdown */}
          <div className="relative">
            <button
              onClick={() => handleFilterClick("size")}
              className={`py-2 px-4 text-sm ${
                activeSize
                  ? "bg-green-500 text-white"
                  : "bg-white text-green-500"
              }`}
            >
              Size
            </button>
            {activeFilter === "size" && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 shadow-lg z-10">
                <ul className="divide-y divide-gray-200">
                  {sizeOptions[activeCategory]?.map((size) => (
                    <li
                      key={size}
                      onClick={() => handleOptionSelect("size", size)}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {size}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Status Dropdown */}
          <div className="relative">
            <button
              onClick={() => handleFilterClick("status")}
              className={`py-2 px-4 text-sm ${
                activeStatus
                  ? "bg-green-500 text-white"
                  : "bg-white text-green-500"
              }`}
            >
              Status
            </button>
            {activeFilter === "status" && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 shadow-lg z-10">
                <ul className="divide-y divide-gray-200">
                  {statusOptions[activeCategory]?.map((status) => (
                    <li
                      key={status}
                      onClick={() => handleOptionSelect("status", status)}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {status}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Display Projects */}
      <div className="bg-gray-100">
        {loading ? (
          <div className="text-center py-10">Loading projects...</div>
        ) : (
          <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 py-10">
            {currentProjects.map((project) => (
              <div key={project.id} className="bg-white p-2 shadow rounded">
                <Link href={`/project/${project.slug}`}>
                  <div className="relative w-full h-64 lg:h-80 overflow-hidden">
                    <img
                      src={project.featuredimage}
                      alt={project.title}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectsPage;
